import { useState, useEffect } from 'react';
import { Link, NavLink, useLocation } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import { Menu, X, ArrowUpRight } from 'lucide-react';
import Logo from '@/components/Logo';
import { useLanguage, LANGS } from '@/context/LanguageContext';
import { EASE } from '@/components/Reveal';

const LINKS = [
  { to: '/', key: 'nav.home', id: 'home' },
  { to: '/about', key: 'nav.about', id: 'about' },
  { to: '/services', key: 'nav.services', id: 'services' },
  { to: '/solutions', key: 'nav.solutions', id: 'solutions' },
  { to: '/offers', key: 'nav.offers', id: 'offers' },
  { to: '/download', key: 'nav.download', id: 'download' },
  { to: '/support', key: 'nav.support', id: 'support' },
  { to: '/contact', key: 'nav.contact', id: 'contact' },
];

export function LangSwitcher({ className = '' }) {
  const { lang, setLang } = useLanguage();
  return (
    <div className={`inline-flex items-center rounded-full border border-brand-line bg-white/80 p-1 ${className}`} data-testid="lang-switcher">
      {Object.keys(LANGS).map((code) => (
        <button
          key={code}
          data-testid={`lang-switcher-${code}`}
          onClick={() => setLang(code)}
          className={`rounded-full px-2.5 py-1 font-mono text-[11px] font-semibold uppercase transition-colors duration-300 ${lang === code ? 'bg-brand-blue text-white' : 'text-brand-steel hover:text-brand-blue'}`}
        >
          {code === 'ku' ? 'KU' : code.toUpperCase()}
        </button>
      ))}
    </div>
  );
}

export default function Navbar() {
  const { t, isRTL } = useLanguage();
  const [open, setOpen] = useState(false);
  const [scrolled, setScrolled] = useState(false);
  const location = useLocation();

  useEffect(() => { setOpen(false); }, [location.pathname]);
  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 24);
    window.addEventListener('scroll', onScroll, { passive: true });
    return () => window.removeEventListener('scroll', onScroll);
  }, []);

  return (
    <>
      <header className={`fixed inset-x-0 top-0 z-50 transition-all duration-500 ${scrolled ? 'glass bg-white/85 border-b border-brand-line/80 shadow-[0_8px_30px_-18px_rgba(15,23,42,0.25)]' : 'bg-transparent border-b border-transparent'}`} data-testid="navbar">
        <div className="mx-auto flex h-16 max-w-[1400px] items-center justify-between gap-3 px-4 lg:px-6">
          <Link to="/" aria-label="WiyakGPS home" data-testid="nav-logo-link"><Logo /></Link>

          <nav className="hidden items-center gap-0.5 lg:flex xl:gap-1">
            {LINKS.map((l) => (
              <NavLink
                key={l.id}
                to={l.to}
                data-testid={`nav-link-${l.id}`}
                className={({ isActive }) => `whitespace-nowrap rounded-full px-2.5 py-2 text-[13px] font-semibold transition-colors duration-300 xl:px-3 ${isActive ? 'text-brand-blue' : 'text-brand-steel hover:text-brand-ink'}`}
              >
                <span className="whitespace-nowrap">{t(l.key)}</span>
              </NavLink>
            ))}
          </nav>

          <div className="flex items-center gap-3">
            <LangSwitcher className="hidden sm:inline-flex" />
            <Link
              to="/login"
              data-testid="nav-login-btn"
              className="hidden items-center gap-1.5 whitespace-nowrap rounded-full border border-brand-line bg-white px-4 py-2 text-sm font-bold text-brand-ink transition-all duration-300 hover:border-brand-blue hover:text-brand-blue md:inline-flex"
            >
              {t('nav.login')}
            </Link>
            <Link
              to="/contact"
              data-testid="nav-cta-btn"
              className="group hidden items-center gap-1.5 whitespace-nowrap rounded-full bg-brand-blue px-4 py-2 text-sm font-bold text-white transition-all duration-300 hover:bg-brand-blueHover hover:shadow-[0_10px_30px_-10px_rgba(0,82,255,0.6)] md:inline-flex"
            >
              {t('nav.cta')}
              <ArrowUpRight className={`h-4 w-4 transition-transform duration-300 group-hover:-translate-y-0.5 ${isRTL ? 'rotate-[-90deg]' : ''}`} />
            </Link>
            <button
              className="grid h-10 w-10 place-items-center rounded-full border border-brand-line bg-white/80 text-brand-ink lg:hidden"
              onClick={() => setOpen(!open)}
              data-testid="nav-mobile-toggle"
              aria-label="Menu"
            >
              {open ? <X className="h-5 w-5" /> : <Menu className="h-5 w-5" />}
            </button>
          </div>
        </div>
      </header>

      <AnimatePresence>
        {open && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            transition={{ duration: 0.3 }}
            className="fixed inset-0 z-40 bg-white/95 glass pt-24 lg:hidden"
            data-testid="nav-mobile-menu"
          >
            <motion.nav
              className="flex flex-col gap-1 px-6"
              initial="hidden" animate="show"
              variants={{ hidden: {}, show: { transition: { staggerChildren: 0.05, delayChildren: 0.1 } } }}
            >
              {LINKS.map((l) => (
                <motion.div key={l.id} variants={{ hidden: { opacity: 0, y: 18 }, show: { opacity: 1, y: 0, transition: { duration: 0.5, ease: EASE } } }}>
                  <NavLink
                    to={l.to}
                    data-testid={`nav-mobile-link-${l.id}`}
                    className={({ isActive }) => `block border-b border-brand-line/70 py-3.5 font-display text-2xl font-bold ${isActive ? 'text-brand-blue' : 'text-brand-ink'}`}
                  >
                    {t(l.key)}
                  </NavLink>
                </motion.div>
              ))}
              <motion.div variants={{ hidden: { opacity: 0, y: 18 }, show: { opacity: 1, y: 0, transition: { duration: 0.5, ease: EASE } } }} className="mt-6 flex items-center justify-between">
                <LangSwitcher />
                <div className="flex items-center gap-2">
                  <Link to="/login" data-testid="nav-mobile-login" className="rounded-full border border-brand-line px-5 py-2.5 text-sm font-bold text-brand-ink">{t('nav.login')}</Link>
                  <Link to="/contact" data-testid="nav-mobile-cta" className="rounded-full bg-brand-blue px-5 py-2.5 text-sm font-bold text-white">{t('nav.cta')}</Link>
                </div>
              </motion.div>
            </motion.nav>
          </motion.div>
        )}
      </AnimatePresence>
    </>
  );
}
